import { useEffect, useRef, useState } from 'react';
import './library-media.css';

export function libraryMediaRatio(format?: string | null): string {
  const numeric = format?.match(/^(\d+(?:\.\d+)?)\s*[:/]\s*(\d+(?:\.\d+)?)$/);
  if (numeric && Number(numeric[1]) > 0 && Number(numeric[2]) > 0) return `${numeric[1]} / ${numeric[2]}`;
  return ({ portrait: '4 / 5', story: '9 / 16', landscape: '16 / 9', banner: '1.91 / 1' } as Record<string, string>)[format?.toLowerCase() ?? ''] ?? '1 / 1';
}

/** Reserve a metadata-sized slot while loading, then trust decoded image dimensions.
 * Imported format labels can be missing or stale; never letterbox a portrait in a square.
 * Mount sources only near their scroll viewport so a large catalog does not
 * start hundreds of downloads while its images are still arriving. */
export function LibraryMedia({ src, alt, format, unavailable = 'Image unavailable' }: {
  src: string;
  alt: string;
  format?: string | null;
  unavailable?: string;
}) {
  const root = useRef<HTMLDivElement>(null);
  const [nearby, setNearby] = useState(false);
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{src:string;width:number;height:number} | null>(null);
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) { setNearby(true); observer.disconnect(); }
    }, { root: element.closest('[data-library-scroll]'), rootMargin: '350px' });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const loaded = loadedSrc === src;
  const failed = failedSrc === src;
  const ratio = dimensions?.src === src ? `${dimensions.width} / ${dimensions.height}` : libraryMediaRatio(format);
  return <div ref={root} className="library-media" data-loading={!loaded && !failed} style={{ aspectRatio: ratio }}>
    {!loaded && !failed && <div className="library-media-placeholder" aria-hidden="true" />}
    {nearby && !failed && <img src={src} alt={alt} loading="lazy" decoding="async" referrerPolicy="no-referrer"
      style={{ opacity: loaded ? 1 : 0 }} onLoad={event => {
        const {naturalWidth, naturalHeight} = event.currentTarget;
        if (naturalWidth > 0 && naturalHeight > 0) setDimensions({src,width:naturalWidth,height:naturalHeight});
        setLoadedSrc(src);
      }} onError={() => setFailedSrc(src)} />}
    {failed && <span className="library-media-unavailable" role="img" aria-label={alt}>{unavailable}</span>}
  </div>;
}
